import { Tooltip } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import {
  defaultLocalTargetingUI,
  ILocalTargetingUI,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/common';
import { LocationForm } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/LocalTargetingBuilder/LocationForm';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

const rowHeight = 32;

export interface ILocalTargetingBuilderProps {
  apiKey: 'LocalTargetingAllowed' | 'LocalTargetingDisallowed';
  logicKey: string;
  uiKey: 'LocalTargetingAllowedUI' | 'LocalTargetingDisallowedUI';
}

// DO NOT REUSE OR COPY/PASTE. THIS IS A CUSTOM SOLUTION COMPONENT
export const LocalTargetingBuilder = ({ apiKey, uiKey }: ILocalTargetingBuilderProps) => {
  const { object, defaultObject } = useFormBuilderStore();

  const [locations, setLocations] = useState<ILocalTargetingUI[]>(
    object[uiKey] as ILocalTargetingUI[],
  );

  const [counter, setCounter] = useState(1);

  useEffect(() => {
    return (): void => {
      object[apiKey] = defaultObject[apiKey];
      object[uiKey] = [cloneDeep(defaultLocalTargetingUI)] as ILocalTargetingUI[];
    };
  }, []);

  const handleAddLocation = () => {
    unstable_batchedUpdates(() => {
      setLocations((prevLocations) => {
        const clonedLocations = cloneDeep(prevLocations);
        clonedLocations.push({
          type: 'country',
          country: '',
          states: '',
          areStatesCommaSeparated: false,
          cities: '',
          areCitiesCommaSeparated: false,
          zips: '',
          dmas: '',
          areDMAsCommaSeparated: false,
          reactKey: counter,
        });
        object[uiKey] = clonedLocations;
        return clonedLocations;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemoveLocation = (idx: number) => {
    setLocations((prevLocations) => {
      const clonedLocations = cloneDeep(prevLocations);
      clonedLocations.splice(idx, 1);
      object[uiKey] = clonedLocations;
      return clonedLocations;
    });
  };

  return (
    <Rows gap={spacing.small} noPadding>
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.huge} style={{ alignItems: 'center', minHeight: `${rowHeight}px` }}>
          <Col>
            <Tooltip
              title='Combine any layer of geo targeting to granularly define the location. When using multiple locations, the logic applied between them will be OR. This means users in any of the defined locations will be targeted.'
              placement='top'
              interactive
            >
              <div style={{ display: 'flex' }}>
                <IconV2 name='info-static' color={publicaColors.gray} />
              </div>
            </Tooltip>
          </Col>
          <Col>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <Text small disableSelect>
                  Logic
                </Text>
              </Col>
              <Col
                displayFlex
                style={{
                  height: '32px',
                  alignItems: 'center',
                }}
              >
                <Text
                  extraSmall
                  bold
                  disableSelect
                  style={{
                    border: `1px solid ${publicaColors.veryDarkGray}`,
                    borderRadius: borderRadius.base,
                    padding: `1px ${spacing.extraSmall} 0`,
                    textAlign: 'center',
                    width: '36px',
                    backgroundColor: publicaColors.veryLightGray,
                  }}
                >
                  OR
                </Text>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <Rows gap={spacing.base}>
          {locations.map((location, i) => {
            return (
              <Row noPadding key={location.reactKey}>
                <LocationForm
                  location={location}
                  index={i}
                  isRemoveDisabled={locations.length === 1 && i === 0}
                  key={location.reactKey}
                  handleRemoveLocation={handleRemoveLocation}
                />
              </Row>
            );
          })}
        </Rows>
      </Row>
      <Row noHorizontalPadding smallVerticalPadding>
        <TextButton size='small' onClick={handleAddLocation} disabled={object[uiKey].length >= 20}>
          + Add Location
        </TextButton>
      </Row>
    </Rows>
  );
};
